/**
 * Grade de horário semanal do funcionário: segunda a domingo, cada dia com entrada, saída e até dois intervalos — ou folga.
 * Daqui saem as horas semanais, o aviso de trabalho noturno, os alertas da CLT (que são lembretes: acordo e convenção
 * coletiva podem dispor diferente) e a descrição da jornada por extenso, que é o que o eSocial pede (dscJorn) e o que vai
 * para o contrato de trabalho.
 */
import { z } from 'zod';
import { Id } from './comum.js';

export const DIAS_DA_SEMANA = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'] as const;
const Hora = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'hora no formato HH:MM');

export const TipoDiaJornada = z.enum(['TRABALHO', 'FOLGA', 'DSR', 'COMPENSADO']);
export type TipoDiaJornada = z.infer<typeof TipoDiaJornada>;
export const ROTULO_TIPO_DIA: Record<TipoDiaJornada, string> = { TRABALHO: 'Trabalha', FOLGA: 'Folga', DSR: 'Descanso semanal (DSR)', COMPENSADO: 'Compensado na semana' };

export const MAX_INTERVALOS = 4;
export const IntervaloJornada = z.object({ inicio: Hora, fim: Hora });
export const DiaJornada = z.object({
  tipo: TipoDiaJornada,
  entrada: Hora.nullable().default(null), saida: Hora.nullable().default(null),
  /** almoço, café da manhã, café da tarde… quantos o dia tiver (até 4) */
  intervalos: z.array(IntervaloJornada).max(MAX_INTERVALOS).default([]),
});
export type DiaJornada = z.infer<typeof DiaJornada>;
/** Sete dias, de segunda (0) a domingo (6). */
export const GradeHorario = z.array(DiaJornada).length(7);
export type GradeHorario = z.infer<typeof GradeHorario>;

const min = (h: string) => Number(h.slice(0, 2)) * 60 + Number(h.slice(3, 5));
/** duração de um trecho, em minutos; saída menor que a entrada = vira o dia (turno da noite) */
const duracao = (de: string, ate: string) => { const d = min(ate) - min(de); return d >= 0 ? d : d + 1440; };

/** Minutos trabalhados no dia (jornada menos os intervalos). 0 para folga ou dia incompleto. */
export function minutosDoDia(d: DiaJornada): number {
  if (d.tipo !== 'TRABALHO' || !d.entrada || !d.saida) return 0;
  const bruto = duracao(d.entrada, d.saida);
  const pausa = d.intervalos.reduce((s, i) => s + duracao(i.inicio, i.fim), 0);
  return Math.max(0, bruto - pausa);
}

/** Minutos trabalhados entre 22h e 5h (hora noturna urbana). */
function minutosNoturnos(d: DiaJornada): number {
  if (d.tipo !== 'TRABALHO' || !d.entrada || !d.saida) return 0;
  const ini = min(d.entrada); const fim = ini + duracao(d.entrada, d.saida);
  // trechos trabalhados = jornada menos intervalos, em minutos corridos a partir da entrada
  const pausas = d.intervalos.map((i) => { let a = min(i.inicio); if (a < ini) a += 1440; return [a, a + duracao(i.inicio, i.fim)] as const; });
  let total = 0;
  for (let m = ini; m < fim; m++) {
    if (pausas.some(([a, b]) => m >= a && m < b)) continue;
    const doDia = m % 1440;
    if (doDia >= 22 * 60 || doDia < 5 * 60) total++;
  }
  return total;
}

export const fmtHoras = (minutos: number): string => `${Math.floor(minutos / 60)}h${minutos % 60 ? String(minutos % 60).padStart(2, '0') : ''}`;

export interface ResumoJornada { minutosSemana: number; horasSemanais: number; minutosPorDia: number[]; temNoturno: boolean; diasTrabalhados: number; avisos: string[] }

export function resumoDaGrade(grade: GradeHorario): ResumoJornada {
  const porDia = grade.map(minutosDoDia);
  const total = porDia.reduce((a, b) => a + b, 0);
  const avisos: string[] = [];
  grade.forEach((d, i) => {
    const nome = DIAS_DA_SEMANA[i]!;
    if (d.tipo !== 'TRABALHO') return;
    if (!d.entrada || !d.saida) { avisos.push(`${nome}: falta a entrada ou a saída`); return; }
    const trabalhado = porDia[i]!;
    const pausa = d.intervalos.reduce((s, x) => s + duracao(x.inicio, x.fim), 0);
    const emOrdem = [...d.intervalos].sort((x, y) => min(x.inicio) - min(y.inicio));
    emOrdem.forEach((x, k) => { const prox = emOrdem[k + 1]; if (prox && min(x.inicio) + duracao(x.inicio, x.fim) > min(prox.inicio)) avisos.push(`${nome}: dois intervalos se encavalam (${x.inicio}–${x.fim} e ${prox.inicio}–${prox.fim})`); });
    for (const x of d.intervalos) { let a = min(x.inicio); const e = min(d.entrada); if (a < e) a += 1440; if (a + duracao(x.inicio, x.fim) > e + duracao(d.entrada, d.saida)) avisos.push(`${nome}: o intervalo está fora do horário de trabalho`); }
    if (trabalhado > 10 * 60) avisos.push(`${nome}: ${fmtHoras(trabalhado)} de trabalho — acima de 10h no dia (8h + 2 extras)`);
    if (trabalhado > 6 * 60 && pausa < 60) avisos.push(`${nome}: mais de 6h de trabalho com intervalo menor que 1h (art. 71 da CLT; acordo ou convenção pode reduzir a 30 min)`);
    else if (trabalhado > 4 * 60 && trabalhado <= 6 * 60 && pausa < 15) avisos.push(`${nome}: entre 4h e 6h de trabalho pede intervalo de 15 min (art. 71, § 1º)`);
    // descanso de 11h até a entrada do dia seguinte trabalhado
    const prox = grade[(i + 1) % 7]!;
    if (prox.tipo === 'TRABALHO' && prox.entrada) {
      const fimHoje = min(d.entrada) + duracao(d.entrada, d.saida);
      const descanso = 1440 + min(prox.entrada) - fimHoje;
      if (descanso < 11 * 60) avisos.push(`${nome} → ${DIAS_DA_SEMANA[(i + 1) % 7]}: só ${fmtHoras(Math.max(0, descanso))} entre as jornadas (o mínimo é 11h — art. 66)`);
    }
  });
  if (total > 44 * 60) avisos.push(`semana com ${fmtHoras(total)} — acima das 44h (art. 7º, XIII, da Constituição)`);
  if (grade.every((d) => d.tipo === 'TRABALHO')) avisos.push('nenhum dia de descanso na semana — marque a folga ou o DSR');
  return { minutosSemana: total, horasSemanais: Math.round((total / 60) * 100) / 100, minutosPorDia: porDia, temNoturno: grade.some((d) => minutosNoturnos(d) > 0), diasTrabalhados: grade.filter((d) => d.tipo === 'TRABALHO').length, avisos };
}

const hh = (h: string) => (h.endsWith(':00') ? `${Number(h.slice(0, 2))}h` : `${Number(h.slice(0, 2))}h${h.slice(3)}`);
function descreverDia(d: DiaJornada): string {
  if (d.tipo === 'FOLGA') return 'folga';
  if (d.tipo === 'DSR') return 'descanso semanal remunerado';
  if (d.tipo === 'COMPENSADO') return 'sem expediente (horas compensadas nos demais dias)';
  if (!d.entrada || !d.saida) return 'horário a definir';
  const pausas = [...d.intervalos].sort((x, y) => min(x.inicio) - min(y.inicio)).map((i) => `das ${hh(i.inicio)} às ${hh(i.fim)}`);
  const lista = pausas.length > 1 ? `${pausas.slice(0, -1).join(', ')} e ${pausas[pausas.length - 1]}` : pausas[0];
  return `das ${hh(d.entrada)} às ${hh(d.saida)}${pausas.length ? `, com ${pausas.length > 1 ? 'intervalos' : 'intervalo'} ${lista}` : ', sem intervalo'}`;
}

/** "Segunda a sexta das 8h às 18h, com intervalo das 12h às 13h12; sábado das 8h às 12h, sem intervalo; domingo: descanso semanal remunerado. Total: 44h semanais." */
export function descreverGrade(grade: GradeHorario): string {
  const textos = grade.map(descreverDia);
  const grupos: Array<{ de: number; ate: number; texto: string }> = [];
  textos.forEach((t, i) => { const u = grupos[grupos.length - 1]; if (u && u.texto === t && u.ate === i - 1) u.ate = i; else grupos.push({ de: i, ate: i, texto: t }); });
  const nome = (i: number) => DIAS_DA_SEMANA[i]!.toLowerCase();
  const partes = grupos.map((g) => {
    const dias = g.de === g.ate ? nome(g.de) : g.ate === g.de + 1 ? `${nome(g.de)} e ${nome(g.ate)}` : `${nome(g.de)} a ${nome(g.ate)}`;
    return /^(folga|descanso|sem expediente|horário a definir)/.test(g.texto) ? `${dias}: ${g.texto}` : `${dias} ${g.texto}`;
  });
  const r = resumoDaGrade(grade);
  const frase = partes.join('; ');
  return `${frase.charAt(0).toUpperCase()}${frase.slice(1)}. Total: ${fmtHoras(r.minutosSemana)} semanais.`;
}

const dia = (entrada: string, saida: string, ...pausas: Array<[string, string]>): DiaJornada => ({ tipo: 'TRABALHO', entrada, saida, intervalos: pausas.map(([inicio, fim]) => ({ inicio, fim })) });
const livre = (tipo: TipoDiaJornada): DiaJornada => ({ tipo, entrada: null, saida: null, intervalos: [] });

/** Pontos de partida: escolhe o mais parecido e ajusta os horários. */
export const GRADES_PRONTAS: ReadonlyArray<{ id: string; rotulo: string; grade: GradeHorario }> = [
  { id: '44-5dias', rotulo: '44h · segunda a sexta (8h48 por dia, sábado compensado)', grade: [...Array.from({ length: 5 }, () => dia('08:00', '17:48', ['12:00', '13:00'])), livre('COMPENSADO'), livre('DSR')] },
  { id: '44-sabado', rotulo: '44h · segunda a sexta 8h + sábado 4h', grade: [...Array.from({ length: 5 }, () => dia('08:00', '17:00', ['12:00', '13:00'])), dia('08:00', '12:00'), livre('DSR')] },
  { id: '40', rotulo: '40h · segunda a sexta, 8h por dia', grade: [...Array.from({ length: 5 }, () => dia('08:00', '17:00', ['12:00', '13:00'])), livre('FOLGA'), livre('DSR')] },
  { id: '36', rotulo: '36h · segunda a sábado, 6h por dia', grade: [...Array.from({ length: 6 }, () => dia('08:00', '14:15', ['11:00', '11:15'])), livre('DSR')] },
  { id: '30', rotulo: '30h · segunda a sexta, 6h por dia (tempo parcial)', grade: [...Array.from({ length: 5 }, () => dia('08:00', '14:15', ['11:00', '11:15'])), livre('FOLGA'), livre('DSR')] },
  { id: '20', rotulo: '20h · segunda a sexta, 4h por dia (aprendiz, estágio)', grade: [...Array.from({ length: 5 }, () => dia('08:00', '12:00')), livre('FOLGA'), livre('DSR')] },
];
export const gradeVazia = (): GradeHorario => Array.from({ length: 7 }, (_, i) => livre(i === 6 ? 'DSR' : 'FOLGA'));

/** Horário pronto do escritório: uma grade salva com nome, para aplicar em qualquer funcionário de qualquer cliente. */
export const GradeModelo = z.object({ id: Id, nome: z.string(), grade: GradeHorario, horasSemanais: z.number(), criadoEm: z.string() });
export type GradeModelo = z.infer<typeof GradeModelo>;
export const GradeModeloBody = z.object({ nome: z.string().trim().min(2).max(80), grade: GradeHorario });
export type GradeModeloBody = z.infer<typeof GradeModeloBody>;
